/**
 * Reusable Area Chart Component
 * Built with Recharts for filled time-series visualization
 */

import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush
} from 'recharts';
import { format } from 'date-fns';

interface ChartSeries {
  name: string;
  dataKey: string;
  color: string;
  unit: string;
  strokeWidth?: number;
  fillOpacity?: number;
}

interface AreaChartProps {
  data: any[];
  series: ChartSeries[];
  height?: number;
  showLegend?: boolean;
  showBrush?: boolean;
  xAxisKey?: string;
  xAxisFormatter?: (value: any) => string;
  yAxisDomain?: [number, number] | ['auto', 'auto'];
  loading?: boolean;
  stacked?: boolean;
}

export function AreaChart({
  data,
  series,
  height = 350,
  showLegend = true,
  showBrush = false,
  xAxisKey = 'timestamp',
  xAxisFormatter = (value) => format(new Date(value), 'HH:mm'),
  yAxisDomain = ['auto', 'auto'],
  loading = false,
  stacked = false
}: AreaChartProps) {

  if (loading) {
    return (
      <div className="animate-pulse bg-slate-800/30 rounded-lg" style={{ height }}>
        <div className="flex items-center justify-center h-full text-slate-500">
          Loading chart data...
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-slate-800/30 rounded-lg flex items-center justify-center" style={{ height }}>
        <p className="text-slate-400">No data available</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsAreaChart
        data={data}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />

        <XAxis
          dataKey={xAxisKey}
          tickFormatter={xAxisFormatter}
          stroke="#94A3B8"
          style={{ fontSize: '12px' }}
        />

        <YAxis
          domain={yAxisDomain}
          stroke="#94A3B8"
          style={{ fontSize: '12px' }}
        />

        <Tooltip
          contentStyle={{
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '8px',
            color: '#E2E8F0'
          }}
          labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy HH:mm')}
          formatter={(value: any) => {
            const numValue = parseFloat(value);
            return isNaN(numValue) ? value : numValue.toFixed(1);
          }}
        />

        {showLegend && (
          <Legend
            wrapperStyle={{ color: '#94A3B8' }}
            iconType="line"
          />
        )}

        {series.map((s) => (
          <Area
            key={s.dataKey}
            type="monotone"
            dataKey={s.dataKey}
            stroke={s.color}
            fill={s.color}
            strokeWidth={s.strokeWidth || 2}
            fillOpacity={s.fillOpacity || 0.3}
            name={`${s.name} (${s.unit})`}
            animationDuration={300}
            stackId={stacked ? '1' : undefined}
          />
        ))}

        {showBrush && (
          <Brush
            dataKey={xAxisKey}
            height={30}
            stroke="#00D4FF"
            fill="rgba(0, 212, 255, 0.1)"
          />
        )}
      </RechartsAreaChart>
    </ResponsiveContainer>
  );
}
